// THE DASHBOARD — the portfolio triage surface.
//
// The operator arrives after a gap holding one question: across everything I am running, what is
// the next move, and where is it done? Three follow-ups: what is on a clock, what moved without
// me, and where does each engagement stand. The page answers all four and then gets out of the
// way — every figure on it is attached to a control that opens the screen where that figure is
// changed, with the engagement already selected (RESEARCH §1.3).
//
//   masthead   the logged-in facts: who, when you were last here, today
//   rail       LIVE · MOVES · CLOCK · BALL · NEW — fixed set, fixed order, invariant geometry
//   zone 1     NEXT MOVE          the rule engine's output, ranked, with its evidence
//   zone 2     THE CLOCKS         dated obligations only, ours and theirs, worst first
//   zone 3     MOVED WITHOUT YOU  the agent and the portal; the operator's own edits are not news
//   zone 4     ENGAGEMENTS        the record surface: position, gate tally, six-cell route grid
//
// THE DETERMINISM MANDATE: every string here is read off the payload. The page ARRANGES. The one
// thing it decides is presentation — lead / rows / disclosure — which is why the queue stays one
// array on the wire.
//
// WHAT IS GONE, and where it went (RESEARCH §3.2): the five stat tiles (they counted rows, not
// moves — the facts survive as queue items and clock rows that name their engagement); the
// per-card Open button (the six-cell grid routes to six destinations instead of one); the
// per-card trash (a destructive cascade is not a peer of a routing control — it lives behind the
// row's ⋯, with the same plan, the same typed token and the same Archive-instead footer).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEngagement } from '../context/EngagementContext.js';
import { useToast } from '../components/Toast.js';
import EmptyState from '../components/EmptyState.js';
import NewEngagementModal from '../components/NewEngagementModal.js';
import { PageHeader, Rail, RailCell, RowMenu, Sep, Sheet, type RailTone } from '../components/PageKit.js';
import { usePoll } from '../hooks/usePoll.js';
import {
  ROUTES, resolveRouteRef, scrollToAnchor, useOpenRoute, type Dest,
} from '../routes.js';
import {
  dashboardApi,
  type DashClock, type DashEngagement, type DashLatest, type DashMove, type DashRailCell,
  type DashRoute, type DashRouteCell,
} from '../api/dashboard.js';
import * as crm from '../api/crm.js';
import { useT, useLocale } from '../i18n/index.js';
import './DashboardPage.de.js';
import './DashboardPage.fr.js';

/** The dashboard's own cadence. The rail is a standing position, not a ticker. */
const DASH_POLL_MS = 15_000;

/** How many moves stand as rows before the disclosure takes over (Hick's law, RESEARCH §2.2). */
const ROWS_SHOWN = 4;

const RAIL_LABEL: Record<string, string> = {
  LIVE: 'Live', MOVES: 'Moves', CLOCK: 'Clock', BALL: 'Ball', NEW: 'New',
};

/**
 * The claim mark, per rule (ATTEST LAW 6). `◇` means agent-authored and not yet committed by a
 * human. A computed tally never carries a mark — which is why no other rule appears here.
 */
const RULE_MARK: Record<string, 'proposed'> = {
  'proposals-pending': 'proposed',
  'conflicts-open': 'proposed',
};

/** The destination a move's control is labelled with — the screen name, not a verb. */
function routeLabel(route: DashRoute, t: ReturnType<typeof useT>): string {
  if (!route) return '';
  const r = route as { kind?: string; screen?: string; anchor?: string };
  if (r.kind === 'in-page') return t('Below');
  if (r.kind === 'related') return t('Open');
  const screen = String(r.screen ?? '');
  if (screen === 'timeline') return t('Timeline');
  if (screen === 'engagements') return t('Client');
  if (!screen) return t('Open');
  return t(screen.charAt(0).toUpperCase() + screen.slice(1).replace('-', ' '));
}

/** Phase labels via t(), except 'Close': the phase must not ride the fleet-wide verb key
 *  ('Schliessen') — it goes through 'Close (phase)', falling back to plain 'Close' in English. */
function tPhase(t: ReturnType<typeof useT>, label: string): string {
  if (label !== 'Close') return t(label);
  const s = t('Close (phase)');
  return s === 'Close (phase)' ? 'Close' : s;
}

const toneClass = (tone: string): string => `dash-tone--${tone}`;

export default function DashboardPage() {
  const nav = useEngagement();
  const { setCurrentId, reload: reloadEngagements } = nav;
  const openRoute = useOpenRoute(nav);
  const toast = useToast();
  const t = useT();
  const { tp, fmtDate, fmtDateTime } = useLocale();

  // The stamp moves `operator_visits.last_seen_at`, so it rides the INITIAL load and nothing
  // else. `usePoll` calls this same function on every tick; the ref is what keeps the poll from
  // walking the "since you last looked" boundary forward every fifteen seconds (F5.4).
  const stamped = useRef(false);
  const load = useCallback(async () => {
    const first = !stamped.current;
    stamped.current = true;
    return dashboardApi.brief({ stamp: first });
  }, []);
  const { data: brief, loading, error, notice, refresh } = usePoll(load, DASH_POLL_MS);

  const [showArchived, setShowArchived] = useState(false);
  const [showAllMoves, setShowAllMoves] = useState(false);
  const [menuFor, setMenuFor] = useState<number | null>(null);
  const [engModalOpen, setEngModalOpen] = useState(false);
  const [clients, setClients] = useState<crm.Client[]>([]);

  // Delete: ask the server for the plan first — a refusal must arrive with its reason.
  const [del, setDel] = useState<{ eng: DashEngagement; plan: crm.EngagementDeletionPlan } | null>(null);
  const [typed, setTyped] = useState('');
  const [deleting, setDeleting] = useState(false);

  // The client list is only needed by the creation form; the brief itself is one call.
  useEffect(() => {
    let cancelled = false;
    crm.listClients().then((rows) => { if (!cancelled) setClients(rows); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (menuFor === null) return;
    const close = () => setMenuFor(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [menuFor]);

  // ── routing ──────────────────────────────────────────────────────────────────────────
  // Every route on this page passes through here. A cell's or a rule's route is a CONSTANT
  // property of that cell or rule, so this is a lookup and never a decision made from a value.
  const go = useCallback(
    (route: DashRoute, clientId?: number | null) => {
      const resolved = resolveRouteRef(route as Record<string, unknown> | null);
      const ownClient = clientId ?? (route as { client_id?: number } | null)?.client_id ?? null;

      if (resolved.kind === 'dest') { void openRoute(resolved.dest); return; }

      if (resolved.kind === 'in-page') {
        const id = resolved.anchor.startsWith('#') ? resolved.anchor.slice(1) : resolved.anchor;
        if (document.getElementById(id)) {
          scrollToAnchor(id);
          const head = document.getElementById(id)?.querySelector('.zone-head');
          if (head) {
            head.classList.add('is-flash');
            window.setTimeout(() => head.classList.remove('is-flash'), 900);
          }
          return;
        }
        // The anchor names a zone on the CLIENT page (rule 30's '#facts'), not one here.
        if (ownClient) { void openRoute(ROUTES.client({ id: ownClient, anchor: id })); return; }
        toast('warn', t('Nothing on this page is anchored at {id}.', { id }));
        return;
      }

      if (resolved.kind === 'client-view') {
        if (!ownClient) { toast('warn', t('This item names a client surface but carries no client.')); return; }
        void openRoute(ROUTES.client({ id: ownClient, anchor: resolved.anchor }));
        return;
      }

      // kind 'none' carries a sentence. Show it — never swallow it.
      toast('warn', resolved.reason);
    },
    [openRoute, toast, t],
  );

  const openCell = useCallback((engagementId: number, cell: DashRouteCell) => {
    const dest: Dest | null =
      cell.screen === 'documents' ? ROUTES.documents({ engagement: engagementId })
        : cell.screen === 'register' ? ROUTES.register({ engagement: engagementId })
          : cell.screen === 'agent' ? ROUTES.agent({ engagement: engagementId })
            : cell.screen === 'proposals' ? ROUTES.proposals({ engagement: engagementId })
              : cell.screen === 'contracting' ? ROUTES.contracting({ engagement: engagementId })
                : cell.screen === 'acceptance' ? ROUTES.acceptance({ engagement: engagementId })
                  : null;
    if (!dest) { toast('warn', t("No page is named '{screen}'.", { screen: cell.screen })); return; }
    void openRoute(dest);
  }, [openRoute, toast, t]);

  // ── the delete plan (kept in substance; only its position moved) ───────────────────────
  const askDelete = useCallback(async (eng: DashEngagement) => {
    try {
      setTyped('');
      setDel({ eng, plan: await crm.engagementDeletionPlan(eng.id) });
    } catch (err) {
      toast('err', (err as Error).message);
    }
  }, [toast]);

  const runDelete = useCallback(async () => {
    if (!del) return;
    setDeleting(true);
    try {
      const out = await crm.deleteEngagement(del.eng.id);
      const gone = out.cascade.map((c) => `${c.count} ${c.what}`).join(', ');
      toast('ok', gone
        ? t('{code} deleted — {gone} went with it', { code: del.eng.code, gone })
        : t('{code} deleted', { code: del.eng.code }));
      setDel(null);
      await Promise.all([reloadEngagements(), refresh()]);
    } catch (err) {
      toast('err', (err as Error).message);
    } finally {
      setDeleting(false);
    }
  }, [del, toast, reloadEngagements, refresh, t]);

  /** The alternative the blocker names, performed rather than merely mentioned. */
  const archiveInstead = useCallback(async () => {
    if (!del) return;
    setDeleting(true);
    try {
      await crm.archiveEngagement(del.eng.id);
      toast('ok', t('{code} archived — every row stays, it just leaves the active list', { code: del.eng.code }));
      setDel(null);
      await Promise.all([reloadEngagements(), refresh()]);
    } catch (err) {
      toast('err', (err as Error).message);
    } finally {
      setDeleting(false);
    }
  }, [del, toast, reloadEngagements, refresh, t]);

  const liveEngagements = useMemo(
    () => (brief?.engagements ?? []).filter((e) => e.archived_at === null),
    [brief],
  );
  const archivedEngagements = useMemo(
    () => (brief?.engagements ?? []).filter((e) => e.archived_at !== null),
    [brief],
  );

  // ── first load / total failure ────────────────────────────────────────────────────────

  if (!brief) {
    if (loading) {
      return (
        <div className="page dash">
          <div className="flex justify-center p-24"><div className="spinner spinner-lg" /></div>
        </div>
      );
    }
    return (
      <div className="page dash">
        <EmptyState
          kind="error"
          title={t('The dashboard brief could not be loaded')}
          detail={(() => {
            const path = '/api/dashboard/brief';
            const s = t('The {path} endpoint answered: “{answer}”', { path, answer: error ?? t('no response') });
            const [pre, post] = s.split(path);
            return <>{pre}<code>{path}</code>{post}</>;
          })()}
          onRetry={() => void refresh()}
        />
      </div>
    );
  }

  // Nothing crossed the boundary: the zone SAYS so with the timestamp and then shows the last
  // three anyway, labelled as such. It never renders as absent — signs of life without a lie
  // about the date (RESEARCH §6, zone 3).
  const quiet = brief.latest.every((e) => !e.new);
  const feed = quiet ? brief.latest.slice(0, 3) : brief.latest;

  const moves = brief.queue.filter((q) => q.rule !== 'all-clear');
  const allClear = brief.queue.find((q) => q.rule === 'all-clear') ?? null;
  const lead = moves[0] ?? null;
  const rest = moves.slice(1);
  const shown = showAllMoves ? rest : rest.slice(0, ROWS_SHOWN);
  const hidden = rest.slice(shown.length);

  return (
    <div className="page dash">
      {/* ── Masthead. Smashing UX p.321: a permanently logged-in home is personal and stateful,
             so the sub-line carries who, when you were last here, and what today is. ── */}
      <PageHeader
        title={t('Dashboard')}
        sub={<>
          <span>{t('signed in as')} <span className="src-ref">{brief.operator.email}</span></span>
          <Sep />
          {brief.operator.last_seen_at
            ? <span>{t('last here')} <span className="voice-figure">{fmtDateTime(brief.operator.last_seen_at)}</span></span>
            : <span className="n-none">{t('first visit')}</span>}
          <Sep />
          <span className="voice-figure">{fmtDate(brief.today)}</span>
          {notice && (<><Sep /><span className="dash-stale">{notice}</span></>)}
        </>}
        verbs={
          /* Deliberately NOT the page's accent: creation is 25 audited events against 116
             proposal reviews. The zone carrying the call to action is NEXT MOVE. */
          <button type="button" className="btn" onClick={() => setEngModalOpen(true)}>
            {t('New engagement')}
          </button>
        }
      />

      <Rail>
        {brief.rail.map((cell) => <RailCellView key={cell.key} cell={cell} onRoute={go} />)}
      </Rail>

      {/* ── Zone 1 · NEXT MOVE ─────────────────────────────────────────────────────────── */}
      <section className="zone" id="z-next">
        <div className="zone-head">
          <span className="u-label">{t('Next move')}</span>
          <span className="zone-note zone-note--end">
            {moves.length
              ? <>{t('Ranked by the rule engine.')} <span className="voice-figure">{moves.length}</span>{' '}
                {tp(moves.length, 'item; every one names its engagement and its screen.', 'items; every one names its engagement and its screen.')}</>
              : t('Nothing in the queue — and the statement below names every gate it is claiming passes.')}
          </span>
        </div>

        {lead ? (
          <>
            <MoveLead move={lead} onRoute={go} />
            {shown.length > 0 && (
              <div className="sheet sheet--clip dash-moves">
                {shown.map((m, i) => <MoveRow key={`${m.rule}:${m.engagement?.id ?? 'x'}:${i}`} move={m} rank={i + 2} onRoute={go} />)}
                {hidden.length > 0 && (
                  <button type="button" className="dash-disclose" onClick={() => setShowAllMoves(true)}>
                    {t('{n} more, lower weight — {titles}', { n: hidden.length, titles: hidden.map((h) => h.title).join(' · ') })}
                  </button>
                )}
                {showAllMoves && rest.length > ROWS_SHOWN && (
                  <button type="button" className="dash-disclose" onClick={() => setShowAllMoves(false)}>
                    {t('Show fewer')}
                  </button>
                )}
              </div>
            )}
          </>
        ) : allClear ? (
          <div className="sheet sheet--edge dash-lead dash-lead--ok">
            <div className="dash-lead-body">
              <div className="dash-lead-title"><span>{allClear.title}</span></div>
              <div className="dash-lead-why">{allClear.why}</div>
              {allClear.chips.length > 0 && (
                <div className="dash-chips">
                  {allClear.chips.map((c) => <span className="chip chip--ok" key={c}><span className="src-ref">{c}</span></span>)}
                </div>
              )}
            </div>
            <div className="dash-lead-route"><span className="dash-rank">{t('rule {rule} · w{weight}', { rule: 'all-clear', weight: 99 })}</span></div>
          </div>
        ) : (
          <EmptyState
            kind="empty"
            title={t('No move can be computed')}
            detail={t('There is no live engagement on file, so no rule has a subject. Nothing can be gated, proposed or contracted until one exists.')}
          >
            <button type="button" className="btn btn-sm" onClick={() => setEngModalOpen(true)}>
              {t('New engagement')}
            </button>
          </EmptyState>
        )}
      </section>

      {/* ── Zone 2 · THE CLOCKS ────────────────────────────────────────────────────────── */}
      <section className="zone" id="z-clocks">
        <div className="zone-head">
          <span className="u-label">{t('The clocks')}</span>
          <span className="zone-note zone-note--end">
            {t('Dated obligations only — ours and theirs. An age with no date is a move, not a clock.')}
          </span>
        </div>
        {brief.clocks.length ? (
          <div className="sheet sheet--clip dash-table">
            <div className="dash-clock dash-clock--head">
              <span className="u-label">{t('What')}</span>
              <span className="u-label dash-right">{t('Due')}</span>
              <span className="u-label dash-right">{t('Standing')}</span>
              <span />
            </div>
            {brief.clocks.map((c) => <ClockRow key={c.key} clock={c} onRoute={go} />)}
          </div>
        ) : (
          <EmptyState
            kind="empty"
            title={t('No clock is running')}
            detail={<>{t('No milestone carries a due date, no acceptance record is issued and no commitment has a date against it. That is a real state, not a missing panel —')}{' '}
              <span className="voice-figure">0</span> {t('dated obligations exist.')}</>}
          />
        )}
      </section>

      {/* ── Zone 3 · MOVED WITHOUT YOU ─────────────────────────────────────────────────── */}
      <section className="zone" id="z-latest">
        <div className="zone-head">
          <span className="u-label">{t('Moved without you')}</span>
          <span className="zone-note zone-note--end">
            {t('Agent runs, portal actions and sign-offs. Your own edits are not news.')}
          </span>
        </div>
        {brief.latest.length ? (
          <div className="sheet sheet--clip dash-feed">
            {quiet && (
              <div className="dash-feed-note">
                {brief.latest_since
                  ? <>{t('Nothing has moved since you were last here')}
                    (<span className="voice-figure">{fmtDateTime(brief.latest_since)}</span>).
                    {' '}{t('The last')} <span className="voice-figure">{feed.length}</span>{' '}
                    {tp(feed.length, 'thing that moved', 'things that moved')} <em>{t('without')}</em> {t('you:')}</>
                  : <>{t('This is your first visit, so nothing can be new yet.')} {t('The last')}{' '}
                    <span className="voice-figure">{feed.length}</span>{' '}
                    {tp(feed.length, 'thing that moved', 'things that moved')} <em>{t('without')}</em> {t('you:')}</>}
              </div>
            )}
            {feed.map((e) => <FeedRow key={e.key} entry={e} onRoute={go} />)}
          </div>
        ) : (
          <EmptyState
            kind="empty"
            title={t('Nothing has moved without you')}
            detail={<>{t('No agent run has closed, no portal user has acted and no sign-off has been recorded')}{brief.latest_since
                ? <> {t('since')} <span className="voice-figure">{fmtDateTime(brief.latest_since)}</span></>
                : ` ${t('— nothing has ever been recorded here')}`}. {t('Your own edits are not listed here; you already know about those.')}</>}
          />
        )}
      </section>

      {/* ── Zone 4 · ENGAGEMENTS ───────────────────────────────────────────────────────── */}
      <section className="zone" id="z-engagements">
        <div className="zone-head">
          <span className="u-label">{t('Engagements')}</span>
          <span className="zone-note zone-note--end">
            {liveEngagements.length ? <>
              <span className="voice-figure">{liveEngagements.length}</span> {t('live')}
              {archivedEngagements.length > 0 && <> · <span className="voice-figure">{archivedEngagements.length}</span> {t('archived')}</>}
            </> : null}
          </span>
          {archivedEngagements.length > 0 && (
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setShowArchived((v) => !v)}>
              {showArchived ? t('Hide archived') : t('Show archived')}
            </button>
          )}
        </div>

        {liveEngagements.length ? (
          <div className="dash-engs">
            {liveEngagements.map((e) => (
              <EngagementRow
                key={e.id} eng={e}
                menuOpen={menuFor === e.id}
                onMenu={(open) => setMenuFor(open ? e.id : null)}
                onCell={openCell}
                onOpenScreen={(dest) => void openRoute(dest)}
                onDelete={() => void askDelete(e)}
              />
            ))}
            {showArchived && archivedEngagements.map((e) => (
              <EngagementRow
                key={e.id} eng={e}
                menuOpen={menuFor === e.id}
                onMenu={(open) => setMenuFor(open ? e.id : null)}
                onCell={openCell}
                onOpenScreen={(dest) => void openRoute(dest)}
                onDelete={() => void askDelete(e)}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            kind="empty"
            title={t('No engagement on file')}
            detail={<>{t('Signet keeps one register per engagement, and every gate, proposal and SOW hangs off one.')} {clients.length
                ? t('The first move is already stated at the top of this page, with its route; it is not repeated here.')
                : t('There is no client on file either, and the engagement form needs one — so the first move is a client, not an engagement.')}</>}
          />
        )}
      </section>

      {/* ── Delete engagement ───────────────────────────────────────────────────────────
          Hand-rolled rather than ConfirmModal because the confirm button has to stay dead
          until the operator has typed the code, and because a refusal needs a footer that
          offers Archive instead of a disabled Delete. */}
      {del && (
        <div className="modal-overlay" onClick={() => !deleting && setDel(null)}>
          <div className="modal" onClick={(ev) => ev.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {del.plan.allowed
                  ? <>{t('Delete')} <span className="src-ref">{del.eng.code}</span></>
                  : <><span className="src-ref">{del.eng.code}</span> {t('cannot be deleted')}</>}
              </h2>
              <button className="close-btn" onClick={() => setDel(null)}>×</button>
            </div>

            <div className="modal-body">
              {!del.plan.allowed && del.plan.blockers.map((b) => (
                <p className="text-sm text-ink-2" key={b.code} style={{ lineHeight: 1.6 }}>{b.message}</p>
              ))}

              {del.plan.allowed && (
                <>
                  <p className="text-sm text-ink-2" style={{ lineHeight: 1.6 }}>
                    <span className="text-ink-1">{del.eng.name}</span> {t("for {client} goes, and so does everything recorded inside it. There is no undo and no archive to fall back on afterwards. Documents are client-owned — only this engagement's", { client: del.eng.client_name })} <strong>{t('links')}</strong> {t('are removed; the files stay in the client library.')}
                  </p>

                  {del.plan.cascade.length > 0 ? (
                    <table className="table table-compact mt-12">
                      <tbody>
                        {del.plan.cascade.map((c) => (
                          <tr key={c.what}>
                            <td className="voice-figure text-right" style={{ width: 60 }}>{c.count}</td>
                            <td className="text-sm text-ink-2">{c.what}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-xs text-ink-3 mt-8">{t('Nothing has been recorded against it yet.')}</p>
                  )}

                  <p className="text-xs text-ink-3 mt-8" style={{ lineHeight: 1.5 }}>
                    {t('The agent workspace under')} <span className="src-ref">data/workspaces/{del.eng.code}/</span> {t('is removed too.')}
                    {del.plan.workspace_busy && (
                      <span className="text-warn"> {t('An extraction run is using it right now, so it will be left in place and has to be cleared by hand.')}</span>
                    )}
                  </p>

                  {del.plan.detaches.map((d) => (
                    <p className="text-sm mt-8" key={d.what} style={{ color: 'var(--warn)', lineHeight: 1.5 }}>
                      <span className="voice-figure">{d.count}</span> {d.what}:{' '}
                      {del.plan.children.map((c) => <span className="src-ref" key={c.id}>{c.code} </span>)}
                    </p>
                  ))}

                  <div className="form-group mt-16 mb-0">
                    <label className="form-label" htmlFor="del-code">
                      {(() => {
                        const token = del.plan.confirm_token ?? '';
                        const s = t('Type {token} to confirm', { token });
                        const at = token ? s.indexOf(token) : -1;
                        return at < 0 ? <>{s}</> : <>{s.slice(0, at)}<span className="src-ref">{token}</span>{s.slice(at + token.length)}</>;
                      })()}
                    </label>
                    <input
                      id="del-code"
                      className="form-input mono"
                      autoFocus
                      autoComplete="off"
                      placeholder={del.plan.confirm_token ?? ''}
                      value={typed}
                      onChange={(ev) => setTyped(ev.target.value)}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-ghost" disabled={deleting} onClick={() => setDel(null)}>{t('Cancel')}</button>
              {del.plan.allowed ? (
                <button
                  className="btn btn-danger"
                  disabled={deleting || typed.trim().toUpperCase() !== (del.plan.confirm_token ?? '')}
                  onClick={() => void runDelete()}
                >
                  {deleting ? t('Deleting…') : t('Delete {code} permanently', { code: del.eng.code })}
                </button>
              ) : (
                <button
                  className="btn btn-accent"
                  disabled={deleting || !!del.eng.archived_at}
                  onClick={() => void archiveInstead()}
                >
                  {del.eng.archived_at ? t('Already archived') : t('Archive instead')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <NewEngagementModal
        open={engModalOpen}
        onClose={() => setEngModalOpen(false)}
        clients={clients}
        engagements={liveEngagements.map((e) => ({ id: e.id, code: e.code, name: e.name, client_id: e.client_id }))}
        onCreated={async (created) => {
          setEngModalOpen(false);
          await Promise.all([reloadEngagements(), refresh()]);
          setCurrentId(created.id);
          toast('ok', t('{code} created', { code: created.code }));
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// The rail
// ---------------------------------------------------------------------------

function RailCellView({ cell, onRoute }: { cell: DashRailCell; onRoute: (r: DashRoute, c?: number | null) => void }) {
  const t = useT();
  const route = cell.route;
  return (
    <RailCell
      label={t(RAIL_LABEL[cell.key] ?? cell.key)}
      go={route ? '→' : undefined}
      value={cell.value}
      unit={cell.unit || undefined}
      tone={cell.tone as RailTone}
      detail={<span className="voice-figure" title={cell.detail}>{cell.detail}</span>}
      onClick={route ? () => onRoute(route) : undefined}
    />
  );
}

// ---------------------------------------------------------------------------
// Next move
// ---------------------------------------------------------------------------

function Mark({ rule }: { rule: string }) {
  const t = useT();
  const mark = RULE_MARK[rule];
  return mark ? <span className={`att att--${mark}`} title={t('proposed by the agent, not yet committed')} /> : null;
}

function MoveTags({ move }: { move: DashMove }) {
  const t = useT();
  return (
    <>
      {move.engagement && <span className="dash-tag src-ref">{move.engagement.code}</span>}
      {move.gate && <span className="dash-gate-tag src-ref">{move.gate}</span>}
      {move.derived && <span className="chip"><span className="src-ref">{t('derived check')}</span></span>}
    </>
  );
}

function MoveLead({ move, onRoute }: { move: DashMove; onRoute: (r: DashRoute, c?: number | null) => void }) {
  const t = useT();
  const broken = move.rule === 'gate-engine-down';
  return (
    <div className={`sheet sheet--edge dash-lead${broken ? ' dash-lead--broken' : ''}`}>
      <div className="dash-lead-body">
        <div className="dash-lead-title">
          <Mark rule={move.rule} />
          <span className={broken ? 'dash-tone--broken' : undefined}>{move.title}</span>
          <MoveTags move={move} />
        </div>
        {/* The page's one reading surface: 15.5px / 1.62 inside 68ch. It is what makes the lead
            the loud thing without a single extra pixel of colour (RESEARCH §5.8). */}
        <div className="dash-lead-why">{move.why}</div>
        {move.chips.length > 0 && (
          <div className="dash-chips">
            {move.chips.map((c, i) => (
              <span className="chip" key={`${c}:${i}`}><span className="src-ref">{c}</span></span>
            ))}
          </div>
        )}
      </div>
      <div className="dash-lead-route">
        <span className="dash-rank">{t('rule {rule} · w{weight}', { rule: move.rule, weight: move.weight })}</span>
        {move.route && (
          <button type="button" className="btn btn-accent" onClick={() => onRoute(move.route, move.client_id ?? null)}>
            {routeLabel(move.route, t)} →
          </button>
        )}
      </div>
    </div>
  );
}

function MoveRow({ move, rank, onRoute }: { move: DashMove; rank: number; onRoute: (r: DashRoute, c?: number | null) => void }) {
  const t = useT();
  return (
    <div className="dash-move">
      <span className="dash-rank">{rank}</span>
      <div className="dash-move-body">
        <div className="dash-move-title">
          <Mark rule={move.rule} />
          <span>{move.title}</span>
          <MoveTags move={move} />
        </div>
        <div className="dash-move-why">{move.why}</div>
      </div>
      <div className="dash-move-act">
        {move.route && (
          <button type="button" className="btn btn-sm" onClick={() => onRoute(move.route, move.client_id ?? null)}>
            {routeLabel(move.route, t)} →
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The clocks
// ---------------------------------------------------------------------------

function ClockRow({ clock, onRoute }: { clock: DashClock; onRoute: (r: DashRoute, c?: number | null) => void }) {
  const t = useT();
  const { fmtDate } = useLocale();
  return (
    <div className="dash-clock">
      <div className="dash-clock-what">
        <div className="dash-clock-name">{clock.what}</div>
        <div className="dash-clock-sub"><span className="src-ref">{clock.ref}</span> · {clock.sub}</div>
      </div>
      <div className="dash-clock-date voice-figure">{fmtDate(clock.due_date)}</div>
      <div className={`dash-clock-delta voice-figure ${toneClass(clock.tone)}`}>{clock.standing}</div>
      <div className="dash-clock-route">
        {clock.route && (
          <button type="button" className="btn btn-sm" onClick={() => onRoute(clock.route, clock.client_id)}>
            {routeLabel(clock.route, t)} →
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Moved without you
// ---------------------------------------------------------------------------

function FeedRow({ entry, onRoute }: { entry: DashLatest; onRoute: (r: DashRoute, c?: number | null) => void }) {
  const t = useT();
  const { fmtDateTime } = useLocale();
  return (
    <div className={`dash-entry${entry.new ? ' dash-entry--new' : ''}`}>
      <div className="dash-entry-when voice-figure">{fmtDateTime(entry.at)}</div>
      <div className="dash-entry-body">
        <div className="dash-entry-what">
          {entry.what}
          {entry.engagement_code && <span className="dash-tag src-ref">{entry.engagement_code}</span>}
        </div>
        <div className="dash-entry-detail">{entry.detail}</div>
        {entry.quote && (
          <div className="ev">
            {entry.quote}
            <div className="ev-src">
              {entry.quote_src.map((s, i) => (
                <span key={s}>
                  {i > 0 && <span className="sep">· </span>}
                  <span className="src-ref">{s}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="dash-entry-act">
        {entry.route && (
          <button type="button" className="btn btn-sm" onClick={() => onRoute(entry.route)}>
            {routeLabel(entry.route, t)} →
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Engagements
// ---------------------------------------------------------------------------

function EngagementRow({
  eng, menuOpen, onMenu, onCell, onOpenScreen, onDelete,
}: {
  eng: DashEngagement;
  menuOpen: boolean;
  onMenu: (open: boolean) => void;
  onCell: (engagementId: number, cell: DashRouteCell) => void;
  onOpenScreen: (dest: Dest) => void;
  onDelete: () => void;
}) {
  const t = useT();
  const { fmtDate } = useLocale();
  const archived = eng.archived_at !== null;
  return (
    <article className={`dash-eng${archived ? ' dash-eng--arch' : ''}`}>
      <div className="dash-eng-head">
        <div className="dash-eng-id-wrap">
          <div className="dash-eng-id">
            <span className="dash-eng-code src-ref">{eng.code}</span>
            <span className="dash-eng-name">{eng.name}</span>
            {archived && (
              <span className="chip">{t('archived')} <span className="voice-figure">{fmtDate(eng.archived_at)}</span></span>
            )}
          </div>
          <div className="dash-eng-meta">
            {eng.client_name} · {t(crm.COMMERCIAL_MODEL_LABELS[eng.commercial_model] ?? eng.commercial_model)}
            {' · '}<span className="src-ref">{eng.currency}</span>
            {eng.last_movement_days !== null && <>
              {' · '}{t('last movement')} <span className="voice-figure">{eng.last_movement_days}</span> {t('d ago')}
            </>}
          </div>
        </div>
        <div className="dash-eng-verbs">
          <RowMenu label={t('More for {code}', { code: eng.code })} open={menuOpen} onOpenChange={onMenu}>
            <button type="button" onClick={() => { onMenu(false); onOpenScreen(ROUTES.lifecycle({ engagement: eng.id })); }}>
              {t('Open the lifecycle')}
            </button>
            <button type="button" onClick={() => { onMenu(false); onOpenScreen(ROUTES.portalAdmin({ engagement: eng.id })); }}>
              {t('Preview as client')}
            </button>
            <button type="button" className="rowmenu-danger" onClick={() => { onMenu(false); onDelete(); }}>
              {t('Delete {code}…', { code: eng.code })}
            </button>
          </RowMenu>
        </div>
      </div>

      <div className="dash-eng-life">
        <div className="dash-track">
          {/* Nine segments; position — never a hue — says where this engagement stands. */}
          <div className="dash-ticks">
            {crm.PHASE_ORDER.map((phase, i) => (
              <span
                key={phase}
                className={`dash-tick${i < eng.phase_index - 1 ? ' is-passed' : i === eng.phase_index - 1 ? ' is-current' : ''}`}
                title={tPhase(t, crm.PHASE_LABELS[phase] ?? phase)}
              />
            ))}
          </div>
          <div className="dash-track-cap">
            {t('phase')} <span className="voice-figure">{eng.phase_index}</span> {t('of')}{' '}
            <span className="voice-figure">{eng.phase_count}</span> ·{' '}
            <span className="dash-phase">{eng.phase_label}</span>
          </div>
        </div>

        <div className="dash-gatebox">
          {eng.gate_skipped ? (
            <div className="dash-gate-line">{eng.gate_skipped}</div>
          ) : eng.gate ? (
            <div className="dash-gate-line">
              <span className="src-ref">{eng.gate.gate}</span> · {eng.gate.title}
              <span className="dash-pips">
                {eng.gate.pips.map((p) => (
                  <span key={p.label} className={`dash-pip${p.met ? ' is-met' : ''}`} title={`${p.label} — ${p.detail}`} />
                ))}
              </span>
              <span className="voice-figure">{eng.gate.auto_met}/{eng.gate.auto_total}</span> {t('automatic')} ·{' '}
              <span className="voice-figure">{eng.gate.manual_met}/{eng.gate.manual_total}</span> {t('manual')}
            </div>
          ) : (
            // B8: a check that did not run is NOT a passed check, and it is not a zero either.
            <div className="dash-gate-line">
              <span className="src-ref">{t('gate')}</span> ·{' '}
              <span className="n-unknown" title={eng.gate_error ?? t('the gate engine did not answer')}>
                {t('tally unknown')}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="dash-routes">
        {eng.routes.map((cell) => (
          <button type="button" className="dash-route" key={cell.key} onClick={() => onCell(eng.id, cell)}>
            <span className="dash-route-k u-label">{t(cell.key)}</span>
            <span className={`dash-route-n voice-figure${cell.tone === 'nil' && cell.count === 0 ? ' is-nil' : ''}`}>
              {cell.mark === 'proposed' && <span className="att att--proposed" />}
              {cell.count}
            </span>
            <span className={`dash-route-q ${toneClass(cell.tone)}`} title={cell.qualifier}>{cell.qualifier}</span>
          </button>
        ))}
      </div>
    </article>
  );
}
